<template>
  <main-layout>
    <!-- 页面标题 -->
    <div class="page-head" slot="header">
      <div class="header-content">
        <div><div class="page-kicker">Accounting</div><h2>Dext import</h2></div>
      </div>
    </div>

    <div class="page-container">
      <!-- 进度条显示区域 -->
      <div v-if="showProgress" class="progress-container">
        <div class="progress-info">
          <span class="progress-text">Download Progress</span>
          <span class="progress-percentage">{{ Math.min(progress, 100) }}%</span>
        </div>
        <el-progress 
          :percentage="Math.min(progress, 100)" 
          :format="format"
          :stroke-width="20"
          :show-text="false"
          class="progress-bar"
        />
      </div>

      <!-- 配置区域 -->
      <section class="page-section">
        <el-card class="config-card">
          <div slot="header" class="config-header">
            <span><i class="el-icon-setting"></i> Download Configuration</span>
          </div>
          
          <!-- Estimated Total Bills Input -->
          <div class="config-section">
            <h3 class="section-title"><i class="el-icon-edit"></i> Estimated Total Bills</h3>
            <el-input-number
              v-model="userTotal"
              :min="1"
              placeholder="Enter estimated total bills"
              :disabled="loading"
              style="margin-bottom: 10px;"
            ></el-input-number>
            <div style="color: #909399; font-size: 13px; margin-bottom: 10px;">If you are not sure, you can estimate a rough value. Progress is for reference only.</div>
          </div>
          
          <!-- Cookie Config -->
          <div class="config-section">
            <h3 class="section-title"><i class="el-icon-key"></i> Authentication</h3>
            <div class="cookie-container">
              <el-input
                v-model="cookie"
                placeholder="Enter Dext authentication cookie"
                class="cookie-input"
                clearable
                :disabled="loading"
                type="password"
                show-password
              >
                <template slot="prepend">Cookie</template>
              </el-input>
              <el-button 
                type="primary" 
                @click="updateCookie" 
                :disabled="loading"
                icon="el-icon-refresh"
              >
                Update Cookie
              </el-button>
            </div>
          </div>
          
          <!-- Account Selection -->
          <div class="config-section">
            <h3 class="section-title"><i class="el-icon-user"></i> Account Selection</h3>
            <div class="account-selector">
              <el-select 
                v-model="selectedAccounts" 
                multiple 
                placeholder="Select accounts to download bills from"
                class="account-select"
                collapse-tags
              >
                <el-option
                  v-for="name in accountOptions"
                  :key="name"
                  :label="name"
                  :value="name"
                ></el-option>
              </el-select>
              <el-button
                type="success"
                icon="el-icon-download"
                @click="handleDownload"
                :disabled="loading || selectedAccounts.length === 0"
                class="download-button"
              >
                Download Files
              </el-button>
            </div>
          </div>
        </el-card>
      </section>

      <!-- 结果检查区域 -->
      <section v-if="showResults" class="page-section">
        <el-card class="results-card">
          <div slot="header" class="results-header">
            <span><i class="el-icon-document"></i> Download Results</span>
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-refresh"
              @click="checkResults"
              :loading="loadingResults"
            >
              Check Results
            </el-button>
          </div>

          <!-- 成功结果 -->
          <div class="result-section">
            <div class="result-title success-title">
              <i class="el-icon-check"></i>
              <span>Successfully Downloaded</span>
              <el-tag type="success" size="small" v-if="results.success.length > 0">
                {{ Number(results.success.reduce((sum, item) => Number(sum) + Number(item.success), 0)) }} items
              </el-tag>
            </div>
            
            <div v-if="results.success.length === 0" class="empty-result">
              <i class="el-icon-info empty-icon"></i>
              <p>No successful downloads yet</p>
            </div>
            
            <el-table 
              v-else 
              :data="results.success" 
              border 
              stripe
              class="result-table"
            >
              <el-table-column prop="account" label="Account Name" min-width="200" show-overflow-tooltip />
              <el-table-column prop="success" label="Success" width="120" />
              <el-table-column prop="failed" label="Failed" width="120" />
            </el-table>
          </div>
          
          <!-- 失败结果 -->
          <div class="result-section">
            <div class="result-title failed-title">
              <i class="el-icon-close"></i>
              <span>Failed Downloads</span>
              <el-tag type="danger" size="small" v-if="results.failed.length > 0">
                {{ results.failed.length }} items
              </el-tag>
            </div>
            
            <div v-if="results.failed.length === 0" class="empty-result">
              <i class="el-icon-info empty-icon"></i>
              <p>No failed downloads</p>
            </div>
            
            <el-table 
              v-else 
              :data="results.failed" 
              border 
              stripe
              class="result-table"
            >
              <el-table-column prop="account_name" label="Account" width="200" show-overflow-tooltip />
              <el-table-column prop="receipt_code" label="Receipt Code" width="150" show-overflow-tooltip />
              <el-table-column prop="invoice_number" label="Invoice Number" width="150" show-overflow-tooltip />
              <el-table-column prop="error_message" label="Error" show-overflow-tooltip />
              <el-table-column prop="created_at" label="Time" width="180" />
            </el-table>
          </div>
        </el-card>
      </section>
    </div>
  </main-layout>
</template>

<script>
import ApiService from "../utils/ApiService";
import ProgressBar from "./ProgressBar.vue";

export default {
  name: "DextBillsDownload",
  components: {
    ProgressBar
  },
  data() {
    return {
      cookie: "", // 用户输入的 Cookie
      loading: false, // 下载按钮加载状态
      loadingResults: false, // 结果加载状态
      selectedAccounts: [], // 选中的账户
      showResults: false, // 是否显示结果
      results: {
        success: [],
        failed: []
      },
      accountOptions: [
        "Sterling Management Services Ltd.",
        "Sterling Maintenance",
        "Sterling Strata",
        "Sterling Utilities",
        "Sterling Work Order",
        "WB"
      ],
      progress: 0,
      showProgress: false,
      pollingInterval: null,
      userTotal: 0 // 新增：用户输入的总数
    };
  },
  methods: {
    async handleDownload() {
      if (this.selectedAccounts.length === 0) {
        this.$message.warning("Please select at least one account!");
        return;
      }

      try {
        this.loading = true;
        await ApiService.post("/dext/bills/download", {
          accounts: this.selectedAccounts
        });

        this.$message.success("Download started successfully.");
        this.showResults = true; // 显示检查结果按钮
        this.startPolling();
      } catch (error) {
        this.$message.error("Download failed: " + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    async checkResults() {
      try {
        this.loadingResults = true;
        const response = await ApiService.get("/dext/bills/status");
        this.results = {
          success: response.status.map(account => ({
            account: account.account_name,
            success: account.success_count,
            failed: account.failed_count
          })),
          failed: response.recent_failures
        };
      } catch (error) {
        this.$message.error("Failed to fetch results: " + (error.response?.data?.message || error.message));
      } finally {
        this.loadingResults = false;
      }
    },

    async updateCookie() {
      if (!this.cookie) {
        this.$message.warning("Please enter a cookie!");
        return;
      }

      try {
        this.loading = true;
        await ApiService.post("/dext/bills/cookie", {
          cookie: this.cookie,
        });
        this.$message.success("Cookie updated successfully!");
      } catch (error) {
        console.error("Cookie update failed:", error);
        this.$message.error("Cookie update failed: " + (error.response?.data?.message || error.message));
      } finally {
        this.loading = false;
      }
    },

    async startPolling() {
      this.showProgress = true;
      this.progress = 0;
      this.pollingInterval = setInterval(async () => {
        try {
          const response = await ApiService.get("/dext/bills/status");
          const success = response.total_success;
          const failed = response.total_failed;
          const total = this.userTotal;
          // 计算实际进度
          const actualProgress = total > 0 ? Math.round(((success + failed) / total) * 100) : 0;
          // 使用平滑动画效果
          if (this.progress < actualProgress) {
            this.progress = Math.min(this.progress + 2, actualProgress);
          }
          if (actualProgress >= 100) {
            this.progress = 100;
            clearInterval(this.pollingInterval);
            // 添加完成状态的标记
            this.$message.success("Download completed successfully!");
          }
        } catch (error) {
          console.error("Polling error:", error);
          clearInterval(this.pollingInterval);
          this.showProgress = false;
          this.$message.error("Error checking download status: " + error.message);
        }
      }, 1000); // 每100ms更新一次，使动画更流畅
    },

    // 添加一个方法来手动隐藏进度条
    hideProgress() {
      this.showProgress = false;
      this.progress = 0;
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    format(percentage) {
      return `${percentage}%`;
    }
  }
};
</script>

<style scoped>
/* 页面布局 */
.page-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.page-section {
  margin-bottom: 25px;
}

/* 配置卡片样式 */
.config-card {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.config-header {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: bold;
}

.config-header i {
  margin-right: 8px;
  color: #409EFF;
}

.config-section {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #ebeef5;
}

.config-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-title {
  font-size: 16px;
  margin-top: 0;
  margin-bottom: 15px;
  color: #606266;
}

.section-title i {
  margin-right: 5px;
  color: #409EFF;
}

/* Cookie 输入区域 */
.cookie-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.cookie-input {
  flex-grow: 1;
}

/* 账户选择区域 */
.account-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.account-select {
  flex-grow: 1;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .cookie-container, .account-selector {
    flex-direction: column;
    align-items: stretch;
  }
  
  .download-button {
    margin-top: 10px;
  }
}

/* 结果卡片样式 */
.results-card {
  margin-top: 20px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.results-header i {
  margin-right: 8px;
  color: #409EFF;
}

.result-section {
  margin-bottom: 30px;
}

.result-section:last-child {
  margin-bottom: 0;
}

.result-title {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
}

.result-title i {
  margin-right: 8px;
}

.result-title span {
  margin-right: 10px;
}

.success-title i {
  color: #67c23a;
}

.failed-title i {
  color: #f56c6c;
}

.empty-result {
  text-align: center;
  padding: 30px 0;
  color: #909399;
}

.empty-icon {
  font-size: 30px;
  margin-bottom: 10px;
}

.result-table {
  width: 100%;
}

/* 进度条样式 */
.progress-container {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.progress-text {
  font-size: 16px;
  font-weight: 500;
  color: #606266;
}

.progress-percentage {
  font-size: 16px;
  font-weight: 600;
  color: #409EFF;
}

.progress-bar {
  margin-top: 10px;
}

.progress-bar >>> .el-progress-bar__outer {
  background-color: #ebeef5;
  border-radius: 10px;
}

.progress-bar >>> .el-progress-bar__inner {
  background-color: #409EFF;
  border-radius: 10px;
  transition: width 0.3s ease;
}
</style>
